import React, { forwardRef, useMemo } from "react";
import { useWorkspacePermissions } from "hooks/useWorkspace";
import { useProjectAuth } from "hooks/project";
import { useProjectsApi } from "hooks/useProjects";
import { useWorkspaceIdentifiers } from "hooks/useWorkspace/useWorkspaceIdentifiers";
import { useProjectIdentifiers } from "hooks/project/useProjectIdentifiers";
import { getUpgradeMessage } from "hooks/useWorkspace/workspaceUpgradeUtils";
import { getProjectUpgradeMessage } from "hooks/project/projectUpgradeUtils";
import { Tooltip } from "components/common/TooltipV2";
import { Skeleton } from "components/common/skeleton";
import { ProjectRole } from "hooks/project/project.types";
import { WorkspaceRole } from "hooks/useWorkspace/workspace.types";
import { Button } from "components/common/buttons/Button";
import {
  Permissions,
  type Permission,
  type WorkspacePermission,
  type ProjectPermission,
} from "utils/permissions/permissionConstants";

type UnifiedRole = WorkspaceRole | ProjectRole;

interface PermissionGuardProps {
  // Context (optional - auto-detected from URL if not provided)
  workspaceId?: string | number;
  projectId?: string | number;

  // Permission checking - accepts Permission type for type safety
  permission?: Permission;
  role?: UnifiedRole;

  // UI behavior
  mode?: "hide" | "disable" | "show" | "tooltip" | "upgrade";
  fallback?: React.ReactNode;
  upgradeMessage?: string;
  upgradeAction?: () => void;
  children: React.ReactElement;

  // Upgrade button customization
  showUpgradeButton?: boolean;
  upgradeButtonText?: string;
  upgradeButtonVariant?: "primary" | "secondary" | "ghost";

  // Direct prop forwarding
  disabled?: boolean;
  // Pass-through props from parents like DropdownMenuTrigger asChild
  // This allows event handlers/refs to reach the underlying child
  [key: string]: unknown;
}

const DOM_PROPS_BLACKLIST = new Set([
  "permission",
  "role",
  "mode",
  "fallback",
  "upgradeMessage",
  "upgradeAction",
  "showUpgradeButton",
  "upgradeButtonText",
  "upgradeButtonVariant",
  "disabled",
  "children",
  "projectId",
  "workspaceId",
]);

const filterDOMProps = (props: Record<string, unknown>): Record<string, unknown> => {
  const filtered: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(props)) {
    if (!DOM_PROPS_BLACKLIST.has(key)) {
      filtered[key] = value;
    }
  }
  return filtered;
};

/**
 * Permission Guard Component
 *
 * A single, intelligent component that handles UI state based on both workspace and project permissions.
 * Automatically detects the context (workspace vs project) and uses the appropriate permission system.
 * Supports trial-aware restrictions and multiple modes: hide, disable, show, and tooltip with upgrade prompts.
 *
 * @example
 * ```tsx
 * import { Permissions } from "utils/permissions/permissionConstants";
 *
 * // Project permissions (auto-detects projectId from URL)
 * <PermissionGuard permission={Permissions.PROJECT_EXPORT} mode="tooltip">
 *   <Button>Export</Button>
 * </PermissionGuard>
 *
 * // Workspace permissions (auto-detects workspaceId from URL)
 * <PermissionGuard permission={Permissions.WORKSPACE_MEMBER_INVITE}>
 *   <InviteMemberButton />
 * </PermissionGuard>
 *
 * // Trial-aware with upgrade action (auto-detects projectId)
 * <PermissionGuard
 *   permission={Permissions.PROJECT_VERSION_CREATE}
 *   mode="tooltip"
 *   upgradeAction={() => setShowUpgradeModal(true)}
 * >
 *   <PublishButton />
 * </PermissionGuard>
 *
 * // Works with dropdown triggers
 * <DropdownMenuTrigger asChild>
 *   <PermissionGuard permission={Permissions.PROJECT_EXPORT} mode="tooltip">
 *     <Button>
 *       <FiDownload className="text-sm" />
 *     </Button>
 *   </PermissionGuard>
 * </DropdownMenuTrigger>
 * ```
 */
export const PermissionGuard = forwardRef<HTMLDivElement, PermissionGuardProps>(
  (
    {
      workspaceId: propWorkspaceId,
      projectId: propProjectId,
      permission,
      role,
      mode = "hide",
      fallback = null,
      upgradeMessage,
      upgradeAction,
      children,
      showUpgradeButton = true,
      upgradeButtonText = "Upgrade",
      upgradeButtonVariant: _upgradeButtonVariant = "ghost",
      disabled: propDisabled = false,
      ...rest
    },
    ref
  ) => {
    // Use provided IDs or auto-detect from URL
    const { workspaceId: urlWorkspaceId } = useWorkspaceIdentifiers();
    const { projectId: urlProjectId } = useProjectIdentifiers();
    const workspaceId =
      (propWorkspaceId as string | number | undefined) ??
      (urlWorkspaceId as string | number | undefined) ??
      undefined;
    const projectId =
      (propProjectId as string | number | undefined) ??
      (urlProjectId as string | number | undefined) ??
      undefined;

    // Determine context and permission type
    const isProjectContext = !!projectId;

    // Determine permission type from the permission string
    // Note: Some permissions starting with "project:" are workspace-level (e.g., WORKSPACE_PROJECT_CREATE)
    // Only check project permissions if we're in a project context
    const permissionString = typeof permission === "string" ? permission : "";
    const isProjectPermission = permissionString
      ? permissionString.startsWith("project:") &&
        isProjectContext &&
        permissionString !== Permissions.WORKSPACE_PROJECT_CREATE
      : false;

    const isWorkspaceScopedPermission =
      Boolean(permissionString) && permissionString.startsWith("workspace:");

    // Use appropriate permission hook
    // Only call useWorkspacePermissions if we have a valid workspaceId
    const workspacePermissions = useWorkspacePermissions(
      workspaceId && workspaceId !== "" ? (workspaceId as string | number) : ""
    );
    const projectAuth = useProjectAuth((projectId as string | number) || undefined);

    // Get project data for trial info and role (only when in project context)
    const { useProject } = useProjectsApi();
    const { data: project, isLoading: isProjectLoading } = useProject(
      (isProjectContext || isProjectPermission) && projectId ? String(projectId) : undefined
    );

    // Extract project role - useProjectAuth already handles role extraction with fallbacks
    const projectRole = useMemo<ProjectRole | null>(() => {
      const roleString = projectAuth.user?.role;
      if (!roleString) return null;

      const role = roleString.toLowerCase();
      if (role === "creator" || role === "editor" || role === "viewer" || role === "trial") {
        return role as ProjectRole;
      }
      return null;
    }, [projectAuth.user?.role]);

    // Unlicensed / legacy "trial" flag for upgrade copy (PermissionGuard passes this to getProjectUpgradeMessage)
    const isTrialProject = useMemo(() => {
      const lic = project?.permissions?.projectInfo?.isLicensed;
      if (typeof lic === "boolean") {
        return !lic;
      }
      return Boolean(
        project?.permissions?.projectInfo?.isTrialProject ?? project?.isTrial ?? false
      );
    }, [project?.permissions?.projectInfo, project?.isTrial]);

    const freeLicensePremiumLocked = useMemo(
      () =>
        Boolean(project?.permissions?.projectInfo?.freeLicensePremiumLocked) ||
        Boolean(project?.permissions?.projectInfo?.unlicensedPremiumLocked) ||
        isTrialProject,
      [project?.permissions?.projectInfo, isTrialProject]
    );

    // Select the appropriate permission system
    const permissions = workspacePermissions;

    // Only include project loading state when we're actually in a project context
    const isLoading =
      permissions.isLoading ||
      (!isWorkspaceScopedPermission && (isProjectContext || isProjectPermission)
        ? projectAuth.isLoading || isProjectLoading
        : false);
    const userRole = isWorkspaceScopedPermission
      ? permissions.role
      : isProjectContext || isProjectPermission
        ? projectRole
        : permissions.role;

    // Show loading state
    if (isLoading) {
      return <Skeleton className="h-8 w-24" />;
    }

    // If no context is available, show children (no permission gating)
    if (!workspaceId && !projectId) {
      return <>{children}</>;
    }

    const workspaceRoleValues = Object.values(WorkspaceRole) as string[];
    const roleUsesWorkspaceRoster =
      role !== undefined && workspaceRoleValues.includes(role as string);

    // Check permissions with proper type handling
    // Use typed canProject/canWorkspace functions for type safety
    const hasPermission = permission
      ? (() => {
          if (isWorkspaceScopedPermission) {
            return workspacePermissions.canWorkspace(permission as WorkspacePermission);
          }
          if (isProjectContext || isProjectPermission) {
            return projectAuth.canProject(permission as ProjectPermission);
          }
          return workspacePermissions.canWorkspace(permission as WorkspacePermission);
        })()
      : true;

    const hasRequiredRole = role
      ? (() => {
          if (roleUsesWorkspaceRoster) {
            return workspacePermissions.hasRole(role as WorkspaceRole);
          }
          if (isProjectContext || isProjectPermission) {
            return projectRole === (role as ProjectRole);
          }
          return workspacePermissions.hasRole(role as WorkspaceRole);
        })()
      : true;
    const canAccess = hasPermission && hasRequiredRole;

    // Determine if the component should be disabled
    const isDisabled = propDisabled || !canAccess;

    // User has access - render children; forward props/ref to preserve asChild semantics
    if (canAccess) {
      if (React.isValidElement(children)) {
        return React.cloneElement(
          children as React.ReactElement<any>,
          {
            ...filterDOMProps(rest),
            ref,
          } as any
        );
      }
      return <>{children}</>;
    }

    // User doesn't have access - handle based on mode
    switch (mode) {
      case "hide":
        return <>{fallback}</>;

      case "disable": {
        const disabledChild: React.ReactNode = React.isValidElement(children)
          ? React.cloneElement(children as React.ReactElement<any>, { disabled: isDisabled } as any)
          : (children as React.ReactNode);
        return (
          <div ref={ref} className="opacity-50 cursor-not-allowed">
            {disabledChild}
          </div>
        );
      }

      case "tooltip": {
        const contextualHelp =
          (isProjectContext || isProjectPermission) && !isWorkspaceScopedPermission
            ? getProjectUpgradeMessage(
                permission as string,
                role as ProjectRole,
                userRole as ProjectRole,
                isTrialProject,
                freeLicensePremiumLocked
              )
            : getUpgradeMessage(
                permission as string,
                role as WorkspaceRole,
                userRole as WorkspaceRole
              );
        const message = upgradeMessage || contextualHelp || "Permission required.";

        const tooltipChild = React.isValidElement(children)
          ? React.cloneElement(children as React.ReactElement<any>, { disabled: isDisabled } as any)
          : (children as React.ReactNode);
        return (
          <Tooltip content={String(message)} className="text-sm max-w-xs">
            <div ref={ref} className="cursor-not-allowed opacity-50">
              {tooltipChild}
            </div>
          </Tooltip>
        );
      }

      case "upgrade": {
        const upgradeChild = React.isValidElement(children)
          ? React.cloneElement(children as React.ReactElement<any>, { disabled: isDisabled } as any)
          : (children as React.ReactNode);
        return (
          <div ref={ref} className="relative group">
            <div className="cursor-not-allowed opacity-50">{upgradeChild}</div>

            {showUpgradeButton && typeof upgradeAction === "function" ? (
              <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
                <Button
                  type="button"
                  color="brand"
                  size="xs"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    upgradeAction();
                  }}
                >
                  {String(upgradeButtonText)}
                </Button>
              </div>
            ) : null}
          </div>
        );
      }

      case "show":
      default:
        if (React.isValidElement(children)) {
          return React.cloneElement(
            children as React.ReactElement<any>,
            {
              ...filterDOMProps(rest),
              ref,
            } as any
          );
        }
        return <>{children}</>;
    }
  }
);

PermissionGuard.displayName = "PermissionGuard";
